<script setup>
import ReportFilterSelector from './FilterSelector.vue';
import { formatTime } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Table from 'dashboard/components/table/Table.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { computed, onMounted, ref, h } from 'vue';

const props = defineProps({
  type: {
    type: String,
    default: 'account',
  },
  getterKey: {
    type: String,
    default: '',
  },
  actionKey: {
    type: String,
    default: '',
  },
  summaryKey: {
    type: String,
    default: '',
  },
  fetchItemsKey: {
    type: String,
    required: true,
  },
});

const store = useStore();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
import { useI18n } from 'vue-i18n';
import SummaryReportLink from './SummaryReportLink.vue';

const rowItems = useMapGetter([props.getterKey]) || [];
const reportMetrics = useMapGetter([props.summaryKey]) || [];

const getMetrics = id =>
  reportMetrics.value.find(metrics => metrics.id === Number(id)) || {};
const columnHelper = createColumnHelper();
const { t } = useI18n();

const RESPONSE_TIME_BUCKETS = [
  { key: 'within5Minutes', label: 'WITHIN_5_MINUTES' },
  { key: 'within10Minutes', label: 'WITHIN_10_MINUTES' },
  { key: 'within20Minutes', label: 'WITHIN_20_MINUTES' },
  { key: 'within30Minutes', label: 'WITHIN_30_MINUTES' },
  { key: 'within45Minutes', label: 'WITHIN_45_MINUTES' },
  { key: 'within1Hour', label: 'WITHIN_1_HOUR' },
  { key: 'within2Hours', label: 'WITHIN_2_HOURS' },
  { key: 'above2Hours', label: 'ABOVE_2_HOURS' },
];

const defaulSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? '' : 'text-n-slate-12',
    },
    cellProps.getValue()
  );

const columns = computed(() => {
  const reportColumns = [
    columnHelper.accessor('name', {
      header: t(`SUMMARY_REPORTS.${props.type.toUpperCase()}`),
      size: 150,
      cell: cellProps => h(SummaryReportLink, cellProps),
    }),
    columnHelper.accessor('conversationsCount', {
      header: t('SUMMARY_REPORTS.CONVERSATIONS'),
      size: 95,
      cell: defaulSpanRender,
    }),
  ];

  if (props.type === 'agent') {
    reportColumns.push(
      columnHelper.accessor('customerMessagesReceived', {
        header: t('SUMMARY_REPORTS.CUSTOMER_MESSAGES_RECEIVED'),
        size: 100,
        cell: defaulSpanRender,
      }),
      columnHelper.accessor('totalPublicOutgoingMessages', {
        header: t('SUMMARY_REPORTS.TOTAL_PUBLIC_OUTGOING_MESSAGES'),
        size: 100,
        cell: defaulSpanRender,
      }),
    );
  }

  if (['agent', 'team'].includes(props.type)) {
    reportColumns.push(
      ...RESPONSE_TIME_BUCKETS.map(bucket =>
        columnHelper.accessor(bucket.key, {
          header: t(`SUMMARY_REPORTS.${bucket.label}`),
          size: 80,
          cell: defaulSpanRender,
        })
      )
    );
  }

  reportColumns.push(
    columnHelper.accessor('avgFirstResponseTime', {
      header: t('SUMMARY_REPORTS.AVG_FIRST_RESPONSE_TIME'),
      size: 105,
      cell: defaulSpanRender,
    }),
    columnHelper.accessor('avgResolutionTime', {
      header: t('SUMMARY_REPORTS.AVG_RESOLUTION_TIME'),
      size: 105,
      cell: defaulSpanRender,
    }),
    columnHelper.accessor('avgReplyTime', {
      header: t('SUMMARY_REPORTS.AVG_REPLY_TIME'),
      size: 105,
      cell: defaulSpanRender,
    }),
    columnHelper.accessor('resolutionsCount', {
      header: t('SUMMARY_REPORTS.RESOLUTION_COUNT'),
      size: 85,
      cell: defaulSpanRender,
    })
  );

  return reportColumns;
});

const renderAvgTime = value => (value ? formatTime(value) : '--');

const renderCount = value => (value ? value.toLocaleString() : '--');

const renderResponseTimeBucket = bucket => {
  const count = Number(bucket?.count || 0);
  const rate = Number(bucket?.rate || 0);
  return `${count.toLocaleString()} (${(rate * 100).toFixed(1)}%)`;
};

const tableData = computed(() =>
  rowItems.value.map(row => {
    const rowMetrics = getMetrics(row.id);
    const {
      conversationsCount,
      customerMessagesReceived,
      totalPublicOutgoingMessages,
      responseTimeDistribution = {},
      avgFirstResponseTime,
      avgResolutionTime,
      avgReplyTime,
      resolvedConversationsCount,
    } = rowMetrics;
    const responseTimeCells = Object.fromEntries(
      RESPONSE_TIME_BUCKETS.map(bucket => [
        bucket.key,
        renderResponseTimeBucket(responseTimeDistribution[bucket.key]),
      ])
    );

    return {
      id: row.id,
      // we fallback on title, label for instance does not have a name property
      name: row.name ?? row.title,
      type: props.type,
      conversationsCount: renderCount(conversationsCount),
      customerMessagesReceived: renderCount(customerMessagesReceived),
      totalPublicOutgoingMessages: renderCount(totalPublicOutgoingMessages),
      ...responseTimeCells,
      avgFirstResponseTime: renderAvgTime(avgFirstResponseTime),
      avgReplyTime: renderAvgTime(avgReplyTime),
      avgResolutionTime: renderAvgTime(avgResolutionTime),
      resolutionsCount: renderCount(resolvedConversationsCount),
    };
  })
);

const fetchAllData = () => {
  store.dispatch(props.fetchItemsKey);
  store.dispatch(props.actionKey, {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  });
};

onMounted(() => fetchAllData());

const onFilterChange = updatedFilter => {
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  businessHours.value = updatedFilter.businessHours;
  fetchAllData();
};

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

// downloadReports method is not used in this component
// but it is exposed to be used in the parent component
const downloadReports = () => {
  const dispatchMethods = {
    agent: 'downloadAgentReports',
    label: 'downloadLabelReports',
    inbox: 'downloadInboxReports',
    team: 'downloadTeamReports',
  };
  if (dispatchMethods[props.type]) {
    const fileName = generateFileName({
      type: props.type,
      to: to.value,
      businessHours: businessHours.value,
    });
    const params = {
      from: from.value,
      to: to.value,
      fileName,
      businessHours: businessHours.value,
    };
    store.dispatch(dispatchMethods[props.type], params);
  }
};

defineExpose({ downloadReports });
</script>

<template>
  <ReportFilterSelector @filter-change="onFilterChange" />
  <div
    class="flex-1 overflow-auto px-2 py-2 mt-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <Table
      :table="table"
      fixed
      type="compact"
      class="w-full text-xs [&_th]:!px-2 [&_td]:!px-2 [&_th]:whitespace-normal [&_td]:whitespace-normal"
    />
  </div>
</template>
